import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Form, Button, Row, Col, Upload, notification, Table, Modal, Card,
    Image, Radio, Menu, Dropdown, Tooltip, Tag, Input, Empty, Space,
    Popconfirm,
    AutoComplete,
    Select
} from "antd";
import { PlusOutlined } from '@ant-design/icons';

import { 
    getHomescreenV2, addHomescreenV2, deleteHomescreenV2, 
    getAllApps, getAllWebClips 
} from "../../../axios/api.device"
import { EditableProTable, ProColumns, ProForm, ProFormSelect, ProFormText, ProHelpSelect } from '@ant-design/pro-components';

import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from "react-i18next";

interface AppItem {
  readonly Key: string;
  readonly Type: 'app';
  readonly Name?: string;
  readonly Value?: string; // Bundle ID
}

interface WebClipItem {
  readonly Key: string;
  readonly Type: 'webclip';
  readonly Name?: string;
  readonly Value?: string; // URL
}

interface FolderItem {
  readonly Key: string;
  readonly Type: 'folder';
  readonly Name: string;
  readonly Children: readonly BaseItem[]; // 子项
}

type BaseItem = AppItem | WebClipItem | FolderItem;

type HomeScreenItem = BaseItem | FolderItem;

interface HomeScreenLayoutDto {
  readonly Dock: readonly HomeScreenItem[];
  readonly Pages: readonly (readonly HomeScreenItem[])[];
}

interface propsModel {
    templateId: string,
    platform: string,
    templateTab: any,
    updateTemplate: () => void,
}

type UpdateParentCallback = (updatedItem: HomeScreenItem) => void;

interface FolderChildColumnProps {
  parentItem: FolderItem;
  appList: any[];
  webClickList: any[];
  onUpdateParent: UpdateParentCallback; // 关键：通用更新回调
  usedValues: Set<string>;
}

export const HomeScreenLayout = (props: propsModel) => {
  const { templateId, platform, templateTab, updateTemplate } = props;
  const [showTable, setShowTable] = useState<number>(0);
  const [appList, setAppList] = useState<any[]>([]);
  const [webClickList, setWebClickList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  
  const [dockItems, setDockItems] = useState<readonly HomeScreenItem[]>([]);
  const [pages, setPages] = useState<readonly (readonly HomeScreenItem[])[]>([[]]);

  const dockItemsRef = useRef(dockItems);
  const pagesRef = useRef(pages);
  useEffect(() => {
    dockItemsRef.current = dockItems;
    pagesRef.current = pages;
  }, [dockItems, pages]);

  var addClick = () => {
    setShowTable(2)
    setDockItems([]);
    setPages([[]]);
  }
  
  function getList() {
    if (platform == "IOS") {
      Promise.all([getHomescreenV2(templateId), getAllApps(), getAllWebClips()]).then(([res, apps, cps]) => {
        if (res) {
          setDockItems(res.Dock);
          setPages(res.Pages);
          setShowTable(2)
        } else {
            setShowTable(1)
        }
        setAppList(apps);
        setWebClickList(cps);
      });
    }
  }

  const getAllUsedValues = (): Set<string> => {
    const used = new Set<string>();

    const collectValues = (items: readonly HomeScreenItem[]) => {
      for (const item of items) {
        if (item.Type === 'app' || item.Type === 'webclip') {
          if (item.Value) used.add(item.Value);
        } else if (item.Type === 'folder') {
          collectValues(item.Children || []);
        }
      }
    };

    collectValues(dockItems);
    for (const page of pages) {
      collectValues(page);
    }

    return used;
  };

  const usedValues = React.useMemo(() => {
    return getAllUsedValues();
  }, [dockItems, pages]); // 依赖项确保响应式更新

  useEffect(() => {
      getList();
  }, []);

type SetStateAction<T> = React.Dispatch<React.SetStateAction<T>>;

interface ColumnHandlers {
  onDelete: (key: string) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  totalItems?: number;
}

const createHomeScreenColumns = (
  handlers: ColumnHandlers,
  appList: any[],
  webClickList: any[],
  usedValues: Set<string>
): ProColumns<HomeScreenItem>[] => [
  {
    title: '类型',
    dataIndex: 'Type',
    width: '15%',
    valueType: 'select',
    valueEnum: {
      app: '应用',
      webclip: '网页剪辑',
      folder: '文件夹',
    },
    renderFormItem: (_, config, form) => {
      const rowKey = config.recordKey;

      return (
        <ProFormSelect
          name={[rowKey, 'Type']}
          options={[
            { label: '应用', value: 'app' },
            { label: '网页剪辑', value: 'webclip' },
            { label: '文件夹', value: 'folder' },
          ]}
          fieldProps={{
            onChange: (value: string) => {
              // 清空 Name 和 Value
              form.setFieldValue([rowKey, 'Name'], undefined);
              form.setFieldValue([rowKey, 'Value'], '');

              if (value === 'folder') {
                form.setFieldValue([rowKey, 'Children'], []);
              } else {
                // 如果切换回非 folder，可选：清除 Children
                form.setFieldValue([rowKey, 'Children'], undefined);
              }
            },
          }}
        />
      );
    },
  },
  {
    title: '名称',
    dataIndex: 'Name',
    width: '20%',
    ellipsis: true,
    render: (_, record) => {
      if (record.Type === 'app') {
        const app = appList.find(a => a.packageName === record.Value);
        if (app && app.name === record.Name) {
          return app.name;
        } else {
          return record.Name || record.Value || '-';
        }
      }
      if (record.Type === 'webclip') {
        const web = webClickList.find(w => w.url === record.Value);
        return web ? web.name : record.Name || record.Value || '-';
      }
      return record.Name || '-';
    },
    renderFormItem: (_, config, form) => {
      const rowKey = config.recordKey;
      const currentType = form.getFieldValue([rowKey, 'Type']) || config.record?.Type;

      const placeholderMap: Record<string, string> = {
        folder: '输入文件夹名称',
        app: '输入名称或选择预设App',
        webclip: '请选择网页剪辑',
      };

      // 动态校验规则
      const getRules = () => {
        return [
          {
            validator: (_: any, value: any) => {
              if (!value || !String(value).trim()) {
                if (currentType === 'folder') {
                  return Promise.reject(new Error('请输入文件夹名称'));
                } else if (currentType === 'app') {
                  return Promise.reject(new Error('请输入或选择应用名称'));
                } else if (currentType === 'webclip') {
                  return Promise.reject(new Error('请选择网页剪辑'));
                }
                //return Promise.reject(new Error('请输入名称'));
              }
              return Promise.resolve();
            },
          },
        ];
      };
      if (currentType === 'folder') {
        return (
          <ProForm.Item
            name={[rowKey, 'Name']}
            rules={getRules()}
            noStyle
          >
            <Input placeholder={placeholderMap.folder}
             />
          </ProForm.Item>
        );
      }

      if (currentType === 'webclip') {
        const availableWebClips = webClickList.filter(clip => !usedValues.has(clip.url));
        const options = availableWebClips.map(item => ({ label: item.name, value: item.url }));
        return (
          <ProForm.Item
            name={[rowKey, 'Name']}
            rules={getRules()}
            noStyle
          >
            <Select
              options={options}
              showSearch
              filterOption={(input, option) => {
              if (!option) return false;
                // 同时搜索 label 和 value
                const label = option.label?.toString().toLowerCase() || '';
                const value = option.value?.toString().toLowerCase() || '';
                const search = input.toLowerCase();
                return label.includes(search) || value.includes(search);
              }}
              onChange={(url: string) => {
                const selected = webClickList.find(w => w.url === url);
                form.setFieldValue([rowKey, 'Value'], url || '');
                form.setFieldValue([rowKey, 'Name'], selected?.name || '');
              }}
              placeholder={placeholderMap.webclip}
            />
          </ProForm.Item>
        );
      }

      if (currentType === 'app') {
        const availableApps = appList.filter(app => !usedValues.has(app.packageName));
        const options = availableApps.map(app => ({
          value: app.packageName,
          label: app.name,
        }));
        return (
          <ProForm.Item
            name={[rowKey, 'Name']}
            rules={getRules()}
            noStyle
          >
            <AutoComplete
              style={{ width: '100%' }}
              options={options}
              showSearch
              placeholder={placeholderMap.app}
              filterOption={(inputValue, option) => {
                if (!option) return false;
                const label = option.label?.toString().toLowerCase() || '';
                const value = option.value?.toString().toLowerCase() || '';
                const search = inputValue.toLowerCase();
                return label.includes(search) || value.includes(search);
              }}
              onSelect={(value: string) => {
                const app = appList.find(a => a.packageName === value);
                form.setFieldValue([rowKey, 'Value'], value);
                form.setFieldValue([rowKey, 'Name'], app?.name || value);
              }}
              onChange={(value: string) => {
                form.setFieldValue([rowKey, 'Name'], value);
              }}
            />
          </ProForm.Item>
        );
      }

      return <Input />;
    },
    dependencies: ['Type'],
  },
  {
    title: 'Bundle ID / URL',
    dataIndex: 'Value',
    width: '35%',
    ellipsis: true,
    render: (_, record) => {
      if (record.Type === 'folder') return '-';
      return record.Value || '-';
    },
    renderFormItem: (_, config, form) => {
      const rowKey = config.recordKey
      ; 
      const currentType = form.getFieldValue([rowKey, 'Type']) || config.record?.Type;
      if (currentType === 'folder' || currentType === 'webclip') {
        return <ProFormText disabled />;
      }

      const getRules = () => {
        return [
          {
            validator: async (_:any, value: any) => {
              // 只有 app 类型需要校验 Bundle ID
              if (currentType !== 'app') {
                return; // 不校验，通过
              }

              // 必填校验
              if (!value || typeof value !== 'string' || !value.trim()) {
                return Promise.reject(new Error('请输入 Bundle ID'));
              }

              const bundleId = value.trim();
              // 获取最新数据
              const dockItems = dockItemsRef.current;
              const pages = pagesRef.current;

              // 收集所有已存在的 Bundle ID（排除自己）
              const existing = new Set<string>();
              const collect = (items: readonly HomeScreenItem[], excludeKey: React.Key) => {
                for (const item of items) {
                  if (item.Key === excludeKey) continue;
                  if (item.Type === 'app' && item.Value) {
                    existing.add(item.Value);
                  } else if (item.Type === 'folder' && item.Children) {
                    collect(item.Children, excludeKey);
                  }
                }
              };

              collect(dockItems, rowKey as React.Key);
              pages.forEach(page => collect(page, rowKey as React.Key));

              if (existing.has(bundleId)) {
                return Promise.reject(
                  new Error(t('Bundle ID "{{bundleId}}" 已存在', { bundleId: value }))
                );
              }
            },
          },
        ];
      };
      return (
        <ProForm.Item
          name={[rowKey, 'Value']}
          rules={getRules()}
          noStyle
        >
          <Input
            onBlur={() => {
              const value = form.getFieldValue([rowKey, 'Value']);
              const currentName = form.getFieldValue([rowKey, 'Name']);
              if (!currentName && value) {
                if (currentType === 'app') {
                  const app = appList.find(a => a.packageName === value);
                  form.setFieldValue([rowKey, 'Name'], app?.name || value);
                } else if (currentType === 'webclip') {
                  const web = webClickList.find(w => w.url === value);
                  form.setFieldValue([rowKey, 'Name'], web?.name || value);
                }
              }
            }}
            placeholder={
              currentType === 'app'
                ? '输入 Bundle ID（如 com.apple.mobilemail）'
                : '输入网页 URL（如 https://example.com）'
            }
          />
        </ProForm.Item>
      );
    },
  },
  {
    title: '操作',
    valueType: 'option',
    width: '30%',
    render: (_, record, index, action) => {
      const { onDelete, onMoveUp, onMoveDown, totalItems } = handlers;

      return [
        <Button
          key="edit"
          type="link"
          onClick={() => action?.startEditable?.(record.Key)}
        >
          编辑
        </Button>,
        <Popconfirm
          key="delete"
          title="确定删除？"
          onConfirm={() => onDelete(record.Key)}
        >
          <Button type="link" danger>
            删除
          </Button>
        </Popconfirm>,
        onMoveUp && (
          <Button
            key="up"
            type="link"
            disabled={index === 0}
            onClick={() => onMoveUp(index)}
          >
            上移
          </Button>
        ),
        onMoveDown && (
          <Button
            key="down"
            type="link"
            disabled={index === (totalItems ?? 0) - 1}
            onClick={() => onMoveDown(index)}
          >
            下移
          </Button>
        ),
      ].filter(Boolean); // 过滤掉 undefined（当无移动功能时）
    },
  },
];

const dockColumns = createHomeScreenColumns(
{
    onDelete: (key) => {
      setDockItems(prev => prev.filter(item => item.Key !== key));
    },
    onMoveUp: (index) => {
      const newItems = [...dockItems];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      setDockItems(newItems);
    },
    onMoveDown: (index) => {
      const newItems = [...dockItems];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      setDockItems(newItems);
    },
    totalItems: dockItems.length,
  },
  appList,
  webClickList,
  usedValues
);

  type SetDockItems = React.Dispatch<
    React.SetStateAction<readonly HomeScreenItem[]>
  >;

  // ===== 子表格专用列（带操作按钮）=====
  const createFolderChildColumns = ({
    parentItem,
    appList,
    webClickList,
    onUpdateParent,
  }: FolderChildColumnProps): ProColumns<BaseItem>[] => {
    const total = parentItem.Children?.length || 0;

    return [
      {
        title: '类型',
        dataIndex: 'Type',
        valueType: 'select',
        valueEnum: {
          app: '应用',
          webclip: '网页剪辑'
        },
        width: '15%',
        renderFormItem: (_, config, form) => {
          const rowKey = config.recordKey;

          return (
            <ProFormSelect
              name={[rowKey, 'Type']}
              options={[
                { label: '应用', value: 'app' },
                { label: '网页剪辑', value: 'webclip' }
              ]}
              fieldProps={{
                onChange: (value: string) => {
                  // 清空 Name 和 Value
                  form.setFieldValue([rowKey, 'Name'], undefined);
                  form.setFieldValue([rowKey, 'Value'], '');

                  if (value === 'folder') {
                    form.setFieldValue([rowKey, 'Children'], []);
                  } else {
                    // 如果切换回非 folder，可选：清除 Children
                    form.setFieldValue([rowKey, 'Children'], undefined);
                  }
                },
              }}
            />
          );
        },
      },
      {
        title: '名称',
        dataIndex: 'Name',
        width: '20%',
        ellipsis: true,
        render: (_, record) => {
          if (record.Type === 'app') {
            const app = appList.find(a => a.packageName === record.Value);
            if (app && app.name === record.Name) {
              return app.name;
            } else {
              return record.Name || record.Value || '-';
            }
          }
          if (record.Type === 'webclip') {
            const web = webClickList.find(w => w.url === record.Value);
            return web ? web.name : record.Name || record.Value || '-';
          }
          return record.Name || '-';
        },
        renderFormItem: (_, config, form) => {
          const rowKey = config.recordKey;
          const currentType = form.getFieldValue([rowKey, 'Type']) || config.record?.Type;

          const placeholderMap: Record<string, string> = {
            app: '输入名称或选择预设App',
            webclip: '请选择网页剪辑',
          };

          // 动态校验规则
          const getRules = () => {
            return [
              {
                validator: (_: any, value: any) => {
                  if (!value || !String(value).trim()) {
                    if (currentType === 'app') {
                      return Promise.reject(new Error('请输入或选择应用名称'));
                    } else if (currentType === 'webclip') {
                      return Promise.reject(new Error('请选择网页剪辑'));
                    }
                    return Promise.reject(new Error('请输入名称'));
                  }
                  return Promise.resolve();
                },
              },
            ];
          };
          if (currentType === 'webclip') {
            const availableWebClips = webClickList.filter(clip => !usedValues.has(clip.url));
            const options = availableWebClips.map(item => ({ label: item.name, value: item.url }));
            return (
              <ProForm.Item
                name={[rowKey, 'Name']}
                rules={getRules()}
                noStyle
              >
                <Select
                  options={options}
                  showSearch
                  filterOption={(input, option) => {
                  if (!option) return false;
                    // 同时搜索 label 和 value
                    const label = option.label?.toString().toLowerCase() || '';
                    const value = option.value?.toString().toLowerCase() || '';
                    const search = input.toLowerCase();
                    return label.includes(search) || value.includes(search);
                  }}
                  onChange={(url: string) => {
                    const selected = webClickList.find(w => w.url === url);
                    form.setFieldValue([rowKey, 'Value'], url || '');
                    form.setFieldValue([rowKey, 'Name'], selected?.name || '');
                  }}
                  placeholder={placeholderMap.webclip}
                />
              </ProForm.Item>
            );
          }

          if (currentType === 'app') {
            const availableApps = appList.filter(app => !usedValues.has(app.packageName));
            const options = availableApps.map(app => ({
              value: app.packageName,
              label: app.name,
            }));
            return (
              <ProForm.Item
                name={[rowKey, 'Name']}
                rules={getRules()}
                noStyle
              >
                <AutoComplete
                  style={{ width: '100%' }}
                  options={options}
                  showSearch
                  placeholder={placeholderMap.app}
                  filterOption={(inputValue, option) => {
                    if (!option) return false;
                    const label = option.label?.toString().toLowerCase() || '';
                    const value = option.value?.toString().toLowerCase() || '';
                    const search = inputValue.toLowerCase();
                    return label.includes(search) || value.includes(search);
                  }}
                  onSelect={(value: string) => {
                    const app = appList.find(a => a.packageName === value);
                    form.setFieldValue([rowKey, 'Value'], value);
                    form.setFieldValue([rowKey, 'Name'], app?.name || value);
                  }}
                  onChange={(value: string) => {
                    form.setFieldValue([rowKey, 'Name'], value);
                  }}
                />
              </ProForm.Item>
            );
          }

          return <Input />;
        },
        dependencies: ['Type'],
      },
      {
        title: 'Bundle ID / URL',
        dataIndex: 'Value',
        width: '35%',
        ellipsis: true,
        render: (_, record) => (record.Type === 'folder' ? '-' : record.Value || '-'),
        renderFormItem: (_, config, form) => {
          const rowKey = config.recordKey
          ; 
          const currentType = form.getFieldValue([rowKey, 'Type']) || config.record?.Type;
          if (currentType === 'folder' || currentType === 'webclip') {
            return <ProFormText disabled />;
          }

          const getRules = () => {
            return [
              {
                validator: async (_:any, value: any) => {
                  // 只有 app 类型需要校验 Bundle ID
                  if (currentType !== 'app') {
                    return; // 不校验，通过
                  }

                  // 必填校验
                  if (!value || typeof value !== 'string' || !value.trim()) {
                    return Promise.reject(new Error('请输入 Bundle ID'));
                  }

                  const bundleId = value.trim();
                  // 获取最新数据
                  const dockItems = dockItemsRef.current;
                  const pages = pagesRef.current;

                  // 收集所有已存在的 Bundle ID（排除自己）
                  const existing = new Set<string>();
                  const collect = (items: readonly HomeScreenItem[], excludeKey: React.Key) => {
                    for (const item of items) {
                      if (item.Key === excludeKey) continue;
                      if (item.Type === 'app' && item.Value) {
                        existing.add(item.Value);
                      } else if (item.Type === 'folder' && item.Children) {
                        collect(item.Children, excludeKey);
                      }
                    }
                  };

                  collect(dockItems, rowKey as React.Key);
                  pages.forEach(page => collect(page, rowKey as React.Key));

                  if (existing.has(bundleId)) {
                    return Promise.reject(
                      new Error(t('Bundle ID "{{bundleId}}" 已存在', { bundleId: value }))
                    );
                  }
                },
              },
            ];
          };
          return (
            <ProForm.Item
              name={[rowKey, 'Value']}
              rules={getRules()}
              noStyle
            >
              <Input
                onBlur={() => {
                  const value = form.getFieldValue([rowKey, 'Value']);
                  const currentName = form.getFieldValue([rowKey, 'Name']);

                  if (!currentName && value) {
                    if (currentType === 'app') {
                      const app = appList.find(a => a.packageName === value);
                      form.setFieldValue([rowKey, 'Name'], app?.name || value);
                    } else if (currentType === 'webclip') {
                      const web = webClickList.find(w => w.url === value);
                      form.setFieldValue([rowKey, 'Name'], web?.name || value);
                    }
                  }
                }}
                placeholder={
                  currentType === 'app'
                    ? '输入 Bundle ID（如 com.apple.mobilemail）'
                    : '输入网页 URL（如 https://example.com）'
                }
              />
            </ProForm.Item>
          );
        },
      },
      {
        title: '操作',
        valueType: 'option',
        width: '30%',
        render: (_, record, index, action) => [
          <Button
            key="edit"
            type="link"
            size="small"
            onClick={() => action?.startEditable?.(record.Key)}
          >
            编辑
          </Button>,
          <Popconfirm
            key="delete"
            title="确定删除？"
            onConfirm={() => {
              const newChildren = (parentItem.Children || []).filter(c => c.Key !== record.Key);
              onUpdateParent({ ...parentItem, Children: newChildren });
            }}
          >
            <Button type="link" size="small" danger>
              删除
            </Button>
          </ Popconfirm>,
          <Button
            key="up"
            type="link"
            size="small"
            disabled={index === 0}
            onClick={() => {
              const children = [...(parentItem.Children || [])];
              [children[index - 1], children[index]] = [children[index], children[index - 1]];
              onUpdateParent({ ...parentItem, Children: children });
            }}
          >
            上移
          </Button>,
          <Button
            key="down"
            type="link"
            size="small"
            disabled={index >= total - 1}
            onClick={() => {
              const children = [...(parentItem.Children || [])];
              [children[index], children[index + 1]] = [children[index + 1], children[index]];
              onUpdateParent({ ...parentItem, Children: children });
            }}
          >
            下移
          </Button>,
        ],
      },
    ];
  };

  // ===== 页面操作 =====
  const addNewPage = () => {
    setPages([...pages, []]);
  };

  const removePage = (index: number) => {
    setPages(pages.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (submitting) return; // 防止重复提交
    
    setSubmitting(true);
    const payload = {
      Dock: dockItems,
      Pages: pages,
    };
    addHomescreenV2(templateId, payload)
    .then(() => {
        updateTemplate();
        notification.success({ message: t("保存成功") })
    }, () => {
        notification.success({ message: t("保存失败") })
    })
    .finally(() => {
      setSubmitting(false);
    });
  };

  var deleteAllClick = () => {
      if (platform == "IOS") {
          deleteHomescreenV2(templateId).then(() => {
              updateTemplate();
              notification.success({ message: t("删除成功") });
              getList();
              setShowTable(1)
          })
      }
  }

    return (
    <>
      <div>
        {/* <Row style={{ margin: '32px 16px 8px 50px', fontSize: '16px' }}>
          <Col className="templateTitle"> {t('主屏布局')}</Col>
        </Row>
        <Row>
          <div className="dividerStyle"></div>
        </Row> */}
        <Row style={{ margin: '16px 16px 16px 0px' }}>
          {showTable === 1 && (
            <div>
              <p style={{ margin: '32px 16px 8px 50px', fontSize: '14px' }}>
                {t('设置主屏布局')}
              </p>
              <div style={{ margin: '32px 16px 8px 50px', fontSize: '16px' }}>
                <Button onClick={addClick} type="primary">
                  <PlusOutlined /> {t('添加规则')}
                </Button>
              </div>
            </div>
          )}

          {showTable === 2 && (
            <>
            <div style={{ width: '90%', margin: '0 auto' }}>
              {/* ===== Dock 区域 ===== */}
              <h4>Dock</h4>
              <EditableProTable<HomeScreenItem>
                value={dockItems}
                onChange={setDockItems}
                columns={dockColumns}
                tableStyle={{ width: '100%' }}
                cardProps={{ bodyStyle: { padding: 0 } }}
                rowKey="Key"
                editable={{
                  type: 'multiple',
                  onSave: async (_key, data) => {
                    return data;
                  },
                  onDelete: async (_key, row) => {
                    return true;
                  },
                  
                  actionRender: (_, __, dom) => [dom.save, dom.cancel],
                }}
                recordCreatorProps={{
                  position: 'bottom',
                  creatorButtonText: '添加',
                  record: () => ({ Key: uuidv4(), Type: 'app', Value: '' }), // 内联新对象
                }}
                pagination={false}
              />

              {/* 渲染 Dock 中的 Folder 子表 */}
              {dockItems.map((item) => {
                if (item.Type === 'folder') {
                  return (
                    <Card
                      key={`dock-folder-${item.Key}`}
                      size="small"
                      title={`📁 ${item.Name}`}
                      style={{ marginLeft: 24, marginTop: 16 }}
                    >
                      <EditableProTable<BaseItem>
                        value={item.Children || []}
                        onChange={(newChildren) => {
                          setDockItems((prev) =>
                            prev.map((i) =>
                              i.Key === item.Key && i.Type === 'folder'
                                ? { ...i, Children: newChildren }
                                : i
                            )
                          );
                        }}
                        //columns={createFolderChildColumns(item, item.Children || [], setDockItems)}
                        columns={createFolderChildColumns({
                          parentItem: item,
                          appList,
                          webClickList,
                          onUpdateParent: (updatedFolder) => {
                            setDockItems(prev =>
                              prev.map(i => (i.Key === updatedFolder.Key ? updatedFolder : i))
                            );
                          },
                          usedValues
                        })}
                        rowKey="Key"
                        pagination={false}
                        editable={{
                          type: 'multiple',
                          onSave: async (_key, data) => {
                            return data;
                          },
                          onDelete: async (_key, row) => {
                            return true;
                          },
                          actionRender: (_, __, dom) => [dom.save, dom.cancel],
                        }}
                        recordCreatorProps={{
                          position: 'bottom',
                          creatorButtonText: '添加',
                          record: () => ({ Key: uuidv4(), Type: 'app', Value: '' }),
                        }}
                      />
                    </Card>
                  );
                }
                return null;
              })}

              {/* ===== Pages 区域 ===== */}
              <h4 style={{ marginTop: 32 }}>Pages</h4>
              {pages.map((pageItems, idx) => {
                const pageColumns = createHomeScreenColumns(
                  {
                    onDelete: (key) => {
                      setPages(prev => {
                        const newPages = [...prev];
                        newPages[idx] = newPages[idx].filter(item => item.Key !== key);
                        return newPages;
                      });
                    },
                    onMoveUp: (index) => {
                      const newPage = [...pageItems];
                      [newPage[index - 1], newPage[index]] = [newPage[index], newPage[index - 1]];
                      setPages(prev => {
                        const newPages = [...prev];
                        newPages[idx] = newPage;
                        return newPages;
                      });
                    },
                    onMoveDown: (index) => {
                      const newPage = [...pageItems];
                      [newPage[index], newPage[index + 1]] = [newPage[index + 1], newPage[index]];
                      setPages(prev => {
                        const newPages = [...prev];
                        newPages[idx] = newPage;
                        return newPages;
                      });
                    },
                    totalItems: pageItems.length,
                  },
                  appList,
                  webClickList,
                  usedValues
                );
                return (<Card
                  key={idx}
                  title={`Page ${idx + 1}`}
                  style={{ marginBottom: 24 }}
                  extra={
                    pages.length > 1 && (
                      <Button size="small" danger onClick={() => removePage(idx)}>
                        删除页面
                      </Button>
                    )
                  }
                >
                  <EditableProTable<HomeScreenItem>
                    value={pageItems}
                    onChange={(newItems) => {
                      const newPages = [...pages];
                      newPages[idx] = newItems;
                      setPages(newPages);
                    }}
                    columns={pageColumns}
                    rowKey="Key"
                    editable={{
                      type: 'multiple',
                      onSave: async (_key, data) => {
                        return data;
                      },
                      onDelete: async (_key, row) => {
                        return true;
                      },
                      actionRender: (_, __, dom) => [dom.save, dom.cancel],
                    }}
                    recordCreatorProps={{
                      position: 'bottom',
                      creatorButtonText: '添加',
                      record: () => ({ Key: uuidv4(), Type: 'app', Value: '' }),
                    }}
                    pagination={false}
                  />

                  {/* 渲染当前 Page 中的 Folder 子表 */}
                  {pageItems.map((item) => {
                    if (item.Type === 'folder') {
                      return (
                        <Card
                          key={`page-${idx}-folder-${item.Key}`}
                          size="small"
                          title={`📁 ${item.Name}`}
                          style={{ marginLeft: 24, marginTop: 16 }}
                        >
                          <EditableProTable<BaseItem>
                            value={item.Children || []}
                            onChange={(newChildren) => {
                              setPages((prev) => {
                                const newPages = [...prev];
                                newPages[idx] = newPages[idx].map((i) =>
                                  i.Key === item.Key && i.Type === 'folder'
                                    ? { ...i, Children: newChildren }
                                    : i
                                );
                                return newPages;
                              });
                            }}
                            columns={createFolderChildColumns({
                              parentItem: item,
                              appList,
                              webClickList,
                              onUpdateParent: (updatedFolder) => {
                                // 更新 pages[idx] 中的这个 folder
                                setPages(prev => {
                                  const newPages = [...prev];
                                  newPages[idx] = newPages[idx].map(i =>
                                    i.Key === updatedFolder.Key ? updatedFolder : i
                                  );
                                  return newPages;
                                });
                              },
                              usedValues
                            })}
                            //columns={createFolderChildColumns(item, item.Children || [], setDockItems)}
                            rowKey="Key"
                            pagination={false}
                            editable={{
                              type: 'multiple',
                              onSave: async (_key, data) => {
                                return data;
                              },
                              onDelete: async (_key, row) => {
                                return true;
                              },
                              actionRender: (_, __, dom) => [dom.save, dom.cancel],
                            }}
                            recordCreatorProps={{
                              position: 'bottom',
                              creatorButtonText: '添加',
                              record: () => ({ Key: uuidv4(), Type: 'app', Value: '' }),
                            }}
                          />
                        </Card>
                      );
                    }
                    return null;
                  })}
                </Card>
               );
              })}

              <Space style={{ marginTop: 16 }}>
                <Button type="dashed" onClick={addNewPage}>
                  + 添加新页面
                </Button>
              </Space>
            </div>
            <div style={{ margin: "32px 16px 8px 50px", fontSize: "20px", textAlign: "center", width: "50%" }}>
                <Button style={{ width: 100, marginLeft: 20 }} type="primary"  loading={submitting} disabled={submitting} onClick={() => { handleSubmit(); }}>{t("保存")}</Button>
                <Button style={{ width: 100, marginLeft: 20 }} type="primary" ghost onClick={() => { deleteAllClick(); }}>{t("删除规则")}</Button>
            </div>
            </>
          )}
        </Row>
      </div>
    </>
  );
};

